/**
 * Billing needs a live token minted by this printer's own Shopify app. A
 * seller's row can fail that two ways: the token is dead (uninstalled or
 * revoked), or it belongs to another printer's app because they sell on
 * more than one Riverr site — Shopify then brands the Approve subscription
 * page after that app.
 *
 * Both are fixed by re-running this printer's OAuth, so we send the seller
 * straight into Shopify and come back to /subscription to finish the
 * upgrade they asked for rather than making them find Stores.
 */

import { addShopAuth } from "../../api/shops";

const PENDING_KEY = "sellerSub.pendingReconnect";
// Renamed from sellerSub.reconnectTried so tabs still holding the old
// departure-time marker aren't stuck behind the guard.
const DONE_KEY = "sellerSub.reconnectCompleted";
const RETURN_TO = "/subscription";

const LOOP_MESSAGE =
  "Reconnecting didn't fix this store's Shopify connection. Try once more, or contact support.";

/** Thrown when we refuse to auto-reconnect again. Callers may retry with force. */
export const RECONNECT_BLOCKED = "RECONNECT_BLOCKED";

function read(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Private-mode Safari can throw; they just land on /subscription.
  }
}

export function clearReconnectGuard() {
  try {
    window.sessionStorage.removeItem(DONE_KEY);
  } catch {
    // nothing to clear
  }
}

/**
 * Call this only after Shopify actually connected the store. A reconnect
 * that never finished — Shopify error, or the seller backed out — must not
 * count, or one bad trip locks them out of retrying in this tab.
 */
export function markReconnectCompleted() {
  write(DONE_KEY, "1");
}

export function stashPendingReconnect(planId) {
  if (!planId) return;
  write(PENDING_KEY, JSON.stringify({ planId, at: Date.now() }));
}

/** Read and clear the plan the seller was upgrading to. */
export function takePendingReconnect() {
  const raw = read(PENDING_KEY);
  try {
    window.sessionStorage.removeItem(PENDING_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** True when /v2/shops/token bounced us back after a successful connect. */
export function reconnectSucceeded(params) {
  return String(params?.get?.("success")) === "True";
}

export function reconnectShopFrom(source) {
  const shops = source?.reconnectShops || source?.shops || [];
  return shops.find((s) => s?.domain) || null;
}

/**
 * Both API layers serialize as `{ message, status, meta }`, so the flag
 * lives under `meta` — reading it off the body only ever returned undefined.
 */
export function needsReauthFromError(err) {
  const body = err?.response?.data || {};
  return !!(body.needsReauth || body.meta?.needsReauth);
}

export function reconnectDomainFromError(err) {
  const body = err?.response?.data || {};
  return body.shopDomain || body.meta?.shopDomain || null;
}

/**
 * Send the seller into this printer's Shopify OAuth. Resolves only if we
 * could not start it — otherwise the browser navigates away.
 */
export async function startShopifyReconnect({ domain, planId, force = false }) {
  if (!domain) throw new Error("No Shopify store to reconnect.");
  // Don't send them back automatically if a completed reconnect already
  // failed to satisfy billing: a printer without its own Partner app mints
  // a token we'd refuse again, and they'd bounce forever. `force` is the
  // seller asking for it by hand, which always wins.
  if (force) {
    clearReconnectGuard();
  } else if (read(DONE_KEY)) {
    const blocked = new Error(LOOP_MESSAGE);
    blocked.code = RECONNECT_BLOCKED;
    throw blocked;
  }
  stashPendingReconnect(planId);
  let url;
  try {
    const res = await addShopAuth({
      platformId: "2",
      url: domain,
      returnTo: RETURN_TO,
    });
    url = res?.data?.url || res?.url;
  } catch (err) {
    takePendingReconnect();
    throw err;
  }
  if (!url) {
    takePendingReconnect();
    throw new Error("Shopify did not return an authorization URL.");
  }
  window.location.href = url;
}
